import { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getNoticeDetails,
    publishNotice,
    archiveNotice,
    deleteNotice,
    deleteNoticeAttachment,
    getNoticeAcknowledgements,
    acknowledgeNotice,
    resendNotice,
} from '../../../redux/actions/SocietyPortal/NoticeAction/NoticeAction';

import { selectedSocietyStorer } from '../../../utils/webStorer';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_NOTICES,
    SOCIETY_PORTAL_NOTICES_EDIT,
    SOCIETY_PORTAL_NOTICES_PRINT,
} from '../../../Routing/routes';
import {
    NOTICE_TYPE_STYLE,
    NOTICE_TYPE_ICON,
    STATUS_STYLE,
    PRIORITY_STYLE,
    CATEGORY_STYLE,
    CATEGORY_ICON,
    AUDIENCE_LABEL,
    humanize,
    fmtDateTime,
    effectiveStatus,
    authorName,
} from './noticeConstants';

const fileIcon = (mime = '') => {
    if (mime.startsWith('image/')) return 'mdi:file-image-outline';
    if (mime.includes('pdf')) return 'mdi:file-pdf-box';
    if (mime.includes('sheet') || mime.includes('excel')) return 'mdi:file-excel-outline';
    if (mime.includes('word') || mime.includes('document')) return 'mdi:file-word-outline';
    return 'mdi:file-outline';
};

const fmtSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ViewNotice = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();
    // The list's kebab deep-links here with ?panel=comments|acknowledgements so
    // those actions land on the relevant section instead of the page top.
    const [searchParams] = useSearchParams();
    const panel = searchParams.get('panel');

    const {
        noticeDetailsData: notice,
        noticeDetailsLoading,
        publishLoading,
        archiveLoading,
        resendLoading,
        deleteNoticeLoading,
        attachmentLoading,
        acknowledgeLoading,
        acknowledgementsData,
        acknowledgementsLoading,
    } = useSelector(state => state.NoticeReducer || {});

    const [confirmDelete, setConfirmDelete] = useState(false);
    const [showReaders, setShowReaders] = useState(panel === 'acknowledgements');

    const refresh = () => dispatch(getNoticeDetails({ id, societyId }));

    useEffect(() => {
        if (id) dispatch(getNoticeDetails({ id, societyId }));
    }, [dispatch, id, societyId]);

    // The reader list is only fetched when the panel is opened — it can be
    // hundreds of rows on a large society and most visits never expand it.
    useEffect(() => {
        if (showReaders && id) dispatch(getNoticeAcknowledgements({ id, societyId }));
    }, [dispatch, showReaders, id, societyId]);

    // Deep-linked panel: scroll once the record is on screen. Guarded on
    // `notice` because the section doesn't exist during the loading state.
    useEffect(() => {
        if (!panel || !notice) return;
        const el = document.getElementById(`notice-panel-${panel}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [panel, notice]);

    const attachments = useMemo(
        () => (Array.isArray(notice?.attachments) ? notice.attachments : []),
        [notice]
    );
    const readers = useMemo(
        () => (Array.isArray(acknowledgementsData) ? acknowledgementsData : (acknowledgementsData?.rows || [])),
        [acknowledgementsData]
    );

    const backToList = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICES);

    if (noticeDetailsLoading && !notice) {
        return (
            <div className="tw-p-5 tw-min-h-screen tw-flex tw-justify-center tw-items-start">
                <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-2 tw-border-gray-200 tw-border-t-sky-500 tw-mt-20" />
            </div>
        );
    }

    if (!notice) {
        return (
            <div className="tw-p-5 tw-min-h-screen">
                <div className="tw-max-w-3xl tw-mx-auto tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-p-8 tw-text-center">
                    <Icon icon="mdi:file-remove-outline" className="tw-text-3xl tw-text-gray-300 tw-mx-auto" />
                    <p className="tw-text-sm tw-text-gray-600 tw-mt-2">This notice could not be found.</p>
                    <button onClick={backToList} className="tw-mt-4 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700 tw-rounded-md">
                        Back to Notices
                    </button>
                </div>
            </div>
        );
    }

    const st = effectiveStatus(notice);
    const ackTotal = notice.target_count;
    const ackCount = notice.acknowledgement_count ?? 0;
    const ackPct = ackTotal ? Math.round((ackCount / ackTotal) * 100) : 0;

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-max-w-5xl tw-mx-auto tw-space-y-4">
                <button
                    onClick={backToList}
                    className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 hover:tw-text-gray-800"
                >
                    <Icon icon="mdi:arrow-left" /> Back to Notices
                </button>

                {/* Header */}
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                    <div className="tw-p-5 tw-border-b tw-border-gray-100">
                        <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
                            <div className="tw-min-w-0">
                                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mb-1.5">
                                    <span className="tw-font-mono tw-text-[11px] tw-font-semibold tw-text-gray-500">{notice.notice_code || '—'}</span>
                                    <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${NOTICE_TYPE_STYLE[notice.notice_type] || NOTICE_TYPE_STYLE.NOTICE}`}>
                                        <Icon icon={NOTICE_TYPE_ICON[notice.notice_type] || NOTICE_TYPE_ICON.NOTICE} className="tw-text-xs" />
                                        {humanize(notice.notice_type)}
                                    </span>
                                    <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${STATUS_STYLE[st] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                        {humanize(st)}
                                    </span>
                                    {notice.is_pinned && (
                                        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full tw-bg-amber-100 tw-text-amber-700">
                                            <Icon icon="mdi:pin" className="tw-text-xs" /> Pinned
                                        </span>
                                    )}
                                </div>
                                <h1 className="tw-text-lg tw-font-bold tw-text-gray-800 tw-leading-snug">{notice.title}</h1>
                                {notice.summary && (
                                    <p className="tw-text-xs tw-text-gray-500 tw-mt-1">{notice.summary}</p>
                                )}
                            </div>
                            <div className="tw-flex tw-items-center tw-gap-2">
                                {st === 'DRAFT' && (
                                    <button
                                        onClick={() => dispatch(publishNotice({ id, onSuccess: refresh }))}
                                        disabled={publishLoading}
                                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-white tw-bg-emerald-600 hover:tw-bg-emerald-700 tw-rounded-md disabled:tw-opacity-50"
                                    >
                                        <Icon icon="mdi:send-outline" className="tw-text-base" />
                                        {publishLoading ? 'Publishing...' : 'Publish'}
                                    </button>
                                )}
                                {(st === 'PUBLISHED' || st === 'EXPIRED') && (
                                    <button
                                        onClick={() => dispatch(archiveNotice({ id, onSuccess: refresh }))}
                                        disabled={archiveLoading}
                                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-700 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50 disabled:tw-opacity-50"
                                    >
                                        <Icon icon="mdi:archive-arrow-down-outline" className="tw-text-base" />
                                        Archive
                                    </button>
                                )}
                                <button
                                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICES_PRINT.replace(':id', id))}
                                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-700 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                                >
                                    <Icon icon="mdi:printer-outline" className="tw-text-base" />
                                    Print
                                </button>
                                {(st === 'PUBLISHED' || st === 'EXPIRED') && !!notice.send_email && (
                                    <button
                                        onClick={() => dispatch(resendNotice({ id, onSuccess: refresh }))}
                                        disabled={resendLoading}
                                        title={notice.resend_count ? `Already resent ${notice.resend_count} time(s)` : 'Email this notice to its audience again'}
                                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-700 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50 disabled:tw-opacity-50"
                                    >
                                        <Icon icon="mdi:email-sync-outline" className="tw-text-base" />
                                        {resendLoading ? 'Sending...' : 'Resend'}
                                    </button>
                                )}
                                <button
                                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICES_EDIT.replace(':id', id))}
                                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-700 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                                >
                                    <Icon icon="mdi:pencil-outline" className="tw-text-base" />
                                    Edit
                                </button>
                                <button
                                    onClick={() => setConfirmDelete(true)}
                                    className="tw-p-2 tw-rounded-md tw-text-rose-600 tw-border tw-border-gray-200 hover:tw-bg-rose-50"
                                    title="Delete"
                                >
                                    <Icon icon="mdi:trash-can-outline" className="tw-text-base" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Meta strip */}
                    <div className="tw-px-5 tw-py-3 tw-bg-gray-50/60 tw-border-b tw-border-gray-100 tw-grid tw-grid-cols-2 sm:tw-grid-cols-4 tw-gap-3 tw-text-[11px]">
                        <div>
                            <p className="tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-semibold tw-text-[10px]">Category</p>
                            <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-mt-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${CATEGORY_STYLE[notice.category] || CATEGORY_STYLE.GENERAL}`}>
                                <Icon icon={CATEGORY_ICON[notice.category] || CATEGORY_ICON.GENERAL} className="tw-text-xs" />
                                {humanize(notice.category)}
                            </span>
                        </div>
                        <div>
                            <p className="tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-semibold tw-text-[10px]">Priority</p>
                            <span className={`tw-inline-flex tw-mt-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${PRIORITY_STYLE[notice.priority] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                {humanize(notice.priority)}
                            </span>
                        </div>
                        <div>
                            <p className="tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-semibold tw-text-[10px]">Audience</p>
                            <p className="tw-text-gray-700 tw-mt-1">
                                {notice.audience === 'CUSTOM'
                                    ? `${(notice.recipients || []).length} specific member${(notice.recipients || []).length === 1 ? '' : 's'}`
                                    : notice.audience === 'FLATS'
                                        ? `${(notice.flats || []).length} specific flat${(notice.flats || []).length === 1 ? '' : 's'}`
                                        : (AUDIENCE_LABEL[notice.audience] || humanize(notice.audience))}
                                {notice.audience !== 'CUSTOM' && notice.audience !== 'FLATS' && Array.isArray(notice.wings) && notice.wings.length > 0 && (
                                    <span className="tw-text-gray-500"> · {notice.wings.map(w => w.title || w.number).join(', ')}</span>
                                )}
                            </p>
                        </div>
                        <div>
                            <p className="tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-semibold tw-text-[10px]">Issued by</p>
                            <p className="tw-text-gray-700 tw-mt-1">{authorName(notice.createdBy)}</p>
                        </div>
                    </div>

                    {/* Dates */}
                    <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-2 tw-text-[11px] tw-text-gray-600">
                        <span className="tw-flex tw-items-center tw-gap-1">
                            <Icon icon="mdi:calendar-plus" className="tw-text-gray-400" />
                            Created {fmtDateTime(notice.created_at)}
                        </span>
                        <span className="tw-flex tw-items-center tw-gap-1">
                            <Icon icon="mdi:send-clock-outline" className="tw-text-gray-400" />
                            {notice.published_at
                                ? `Published ${fmtDateTime(notice.published_at)}`
                                : notice.publish_at
                                    ? `Scheduled for ${fmtDateTime(notice.publish_at)}`
                                    : 'Not published'}
                        </span>
                        {notice.expires_at && (
                            <span className={`tw-flex tw-items-center tw-gap-1 ${st === 'EXPIRED' ? 'tw-text-amber-700 tw-font-semibold' : ''}`}>
                                <Icon icon="mdi:calendar-remove-outline" className="tw-text-gray-400" />
                                Expires {fmtDateTime(notice.expires_at)}
                            </span>
                        )}
                    </div>

                    {notice.audience === 'CUSTOM' && Array.isArray(notice.recipients) && notice.recipients.length > 0 && (
                        <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                            <p className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-400 tw-font-semibold tw-mb-1.5">
                                Sent to
                            </p>
                            <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                                {notice.recipients.map(u => (
                                    <span
                                        key={u.user_id}
                                        title={u.email || ''}
                                        className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-bg-sky-50 tw-text-sky-800"
                                    >
                                        <Icon icon="mdi:account-outline" className="tw-text-xs" />
                                        {authorName(u)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {notice.audience === 'FLATS' && Array.isArray(notice.flats) && notice.flats.length > 0 && (
                        <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                            <p className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-400 tw-font-semibold tw-mb-1.5">
                                Sent to flats
                            </p>
                            <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                                {notice.flats.map(f => (
                                    <span
                                        key={f.flat_id}
                                        className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-bg-sky-50 tw-text-sky-800"
                                    >
                                        <Icon icon="mdi:door" className="tw-text-xs" />
                                        Flat {f.flat_number}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Body — trusted HTML authored by society staff in the editor */}
                    <div className="tw-p-5">
                        <div
                            className="tw-prose tw-prose-sm tw-max-w-none tw-text-sm tw-text-gray-700"
                            dangerouslySetInnerHTML={{ __html: notice.body || '' }}
                        />
                    </div>
                </div>

                {/* Attachments */}
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm">
                    <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
                        <h2 className="tw-text-sm tw-font-bold tw-text-gray-800 tw-flex tw-items-center tw-gap-1.5">
                            <Icon icon="mdi:paperclip" className="tw-text-gray-400" />
                            Attachments
                            <span className="tw-text-[11px] tw-font-normal tw-text-gray-400">({attachments.length})</span>
                        </h2>
                        {/* No upload here — files are added on the edit form. This is a
                            read screen; the only write it keeps is removing a file that
                            is already listed. */}
                    </div>
                    <div className="tw-p-4">
                        {attachments.length === 0 ? (
                            <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-4">No attachments</p>
                        ) : (
                            <ul className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-2">
                                {attachments.map(a => (
                                    <li key={a.attachment_id} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-3 tw-py-2 tw-border tw-border-gray-100 tw-rounded-lg">
                                        <a
                                            href={a.file_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-text-xs tw-text-gray-700 hover:tw-text-sky-700"
                                        >
                                            <Icon icon={fileIcon(a.mime_type)} className="tw-text-lg tw-text-gray-400 tw-shrink-0" />
                                            <span className="tw-truncate" title={a.file_name}>{a.file_name}</span>
                                            {a.size ? <span className="tw-text-[10px] tw-text-gray-400 tw-shrink-0">{fmtSize(a.size)}</span> : null}
                                        </a>
                                        <button
                                            title="Remove"
                                            disabled={attachmentLoading}
                                            onClick={() => dispatch(deleteNoticeAttachment({
                                                id,
                                                attachmentId: a.attachment_id,
                                                onSuccess: refresh,
                                            }))}
                                            className="tw-p-1 tw-rounded tw-text-rose-600 hover:tw-bg-rose-50 disabled:tw-opacity-50"
                                        >
                                            <Icon icon="mdi:close" className="tw-text-sm" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Acknowledgements */}
                {notice.requires_acknowledgement && (
                    <div id="notice-panel-acknowledgements" className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm">
                        <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                            <div>
                                <h2 className="tw-text-sm tw-font-bold tw-text-gray-800 tw-flex tw-items-center tw-gap-1.5">
                                    <Icon icon="mdi:check-decagram-outline" className="tw-text-gray-400" />
                                    Acknowledgements
                                </h2>
                                <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                                    {ackCount} of {ackTotal ?? '—'} residents have confirmed reading this
                                </p>
                            </div>
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <button
                                    onClick={() => dispatch(acknowledgeNotice({ id, onSuccess: refresh }))}
                                    disabled={acknowledgeLoading || !!notice.acknowledged_by_me}
                                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-emerald-700 tw-bg-emerald-50 hover:tw-bg-emerald-100 tw-rounded-md disabled:tw-opacity-50"
                                >
                                    <Icon icon="mdi:check" className="tw-text-base" />
                                    {notice.acknowledged_by_me ? 'You have read this' : 'Mark as read'}
                                </button>
                                <button
                                    onClick={() => setShowReaders(v => !v)}
                                    className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-700 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                                >
                                    {showReaders ? 'Hide readers' : 'Who has read it'}
                                </button>
                            </div>
                        </div>
                        {ackTotal ? (
                            <div className="tw-px-4 tw-pt-3">
                                <div className="tw-h-1.5 tw-w-full tw-bg-gray-100 tw-rounded-full tw-overflow-hidden">
                                    <div className="tw-h-full tw-bg-emerald-500" style={{ width: `${ackPct}%` }} />
                                </div>
                                <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">{ackPct}% acknowledged</p>
                            </div>
                        ) : null}
                        {showReaders && (
                            <div className="tw-p-4">
                                {acknowledgementsLoading ? (
                                    <div className="tw-flex tw-justify-center tw-py-6">
                                        <div className="tw-animate-spin tw-rounded-full tw-h-5 tw-w-5 tw-border-2 tw-border-gray-200 tw-border-t-sky-500" />
                                    </div>
                                ) : readers.length === 0 ? (
                                    <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-4">Nobody has acknowledged this yet</p>
                                ) : (
                                    <ul className="tw-divide-y tw-divide-gray-50">
                                        {readers.map(r => (
                                            <li key={r.acknowledgement_id || r.user_id} className="tw-flex tw-items-center tw-justify-between tw-py-2 tw-text-xs">
                                                <span className="tw-text-gray-700">
                                                    {authorName(r.user || r)}
                                                    {r.flat?.flat_number && (
                                                        <span className="tw-text-gray-400"> · Flat {r.flat.flat_number}</span>
                                                    )}
                                                </span>
                                                <span className="tw-text-gray-400">{fmtDateTime(r.acknowledged_at)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                )}

            </div>

            {confirmDelete && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-sm tw-overflow-hidden">
                        <div className="tw-p-4 tw-border-b tw-border-gray-100">
                            <h3 className="tw-text-sm tw-font-bold tw-text-gray-800">Delete Notice?</h3>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">This action cannot be undone.</p>
                        </div>
                        <div className="tw-p-5">
                            <p className="tw-text-xs tw-text-gray-600 tw-mb-4">
                                You are about to delete <span className="tw-font-semibold">{notice.notice_code || 'this notice'}</span> — {notice.title}.
                            </p>
                            <div className="tw-flex tw-justify-end tw-gap-2">
                                <button onClick={() => setConfirmDelete(false)} className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">Cancel</button>
                                <button
                                    onClick={() => dispatch(deleteNotice({ id, onSuccess: backToList }))}
                                    disabled={deleteNoticeLoading}
                                    className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50"
                                >
                                    {deleteNoticeLoading ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewNotice;
